/** One of Autodesk's own toolbar glyphs, drawn from the icon font the
 *  viewer's stylesheet ships (font-family adsk-viewing, applied to any element
 *  whose class starts with adsk-icon-). Used on the Autodesk surface so the
 *  tools Autodesk draws wear Autodesk's glyphs; the built-in canvas never
 *  loads that stylesheet, so the shared icon stands in until it is on the
 *  page and everywhere else. The class names are Autodesk's toolbar chrome,
 *  not a documented API: see the API register before adding one. */
import { useEffect, useState } from 'react';
import { cn } from '../../shared/ui/utils';
import type { AdskGlyphName } from '../../shared-ui/takeoff/adskGlyphName';

export type { AdskGlyphName };

/** The <link> loadViewerSdk attaches (the only style.min.css on the page).
 *  Cross-origin, so its rules cannot be read; `sheet` turning non-null is
 *  the load signal. */
export const ADSK_VIEWER_STYLESHEET_SELECTOR = 'link[href$="/style.min.css"]';

function stylesheetLoaded(): boolean {
  return !!document.querySelector<HTMLLinkElement>(ADSK_VIEWER_STYLESHEET_SELECTOR)?.sheet;
}

/** Keep the shared icon until the actual font loads, not just its stylesheet. */
export function useAdskViewerStylesReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let loading = false;
    let ticks = 0;
    const checkFont = async () => {
      if (loading || !stylesheetLoaded() || !document.fonts) return;
      loading = true;
      try {
        const faces = await document.fonts.load('18px "adsk-viewing"');
        if (!cancelled && faces.length > 0 && faces.every(face => face.status === 'loaded')) {
          setReady(true);
          window.clearInterval(timer);
        }
      } catch {
        // Network/font failures retain the readable SVG fallback.
      } finally {
        loading = false;
      }
    };
    const timer = window.setInterval(() => {
      if (++ticks > 80) window.clearInterval(timer);
      else void checkFont();
    }, 250);
    void checkFont();
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);
  return ready;
}

export function AdskGlyph({
  name,
  fallback: Fallback,
  className,
}: {
  name: AdskGlyphName;
  fallback: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  const ready = useAdskViewerStylesReady();
  if (!ready) return <Fallback className={className} />;
  // The glyph is the ::before content, sized by font-size; a caller's text
  // class wins over the 18px default so menu rows can shrink it.
  return (
    <span
      aria-hidden="true"
      style={{ fontFamily: 'adsk-viewing' }}
      className={cn(`adsk-icon-${name}`, 'inline-flex shrink-0 items-center justify-center leading-none text-[18px]', className)}
    />
  );
}
